import React, { useEffect, useMemo } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  ChevronRight,
  Compass,
  HelpCircle,
  PlayCircle,
  Sparkles,
  Video,
} from 'lucide-react';
import {
  buildAbsoluteUrl,
  buildBreadcrumbs,
  buildFirstAidKitPath,
  buildHowToPath,
  buildReelsHubPath,
  buildSubjectPath,
  buildTopicPath,
  createAlternatePaths,
  createCourseJsonLd,
  createFaqJsonLd,
  createFirstAidHeading,
  createHowToHeading,
  createHowToJsonLd,
  createVideoJsonLd,
  DEFAULT_SEO_LOCALE,
  formatGradeLabel,
  getFirstAidSummary,
  getHowToSummary,
  getLocaleLabel,
  getRelatedTopics,
  getReelsSummary,
  getSeoLocale,
  getSubjectByParams,
  getSubjectSummary,
  getTopicByHowToSlug,
  getTopicByParams,
  getTopicPreviewItems,
  getTopicSummary,
  isSupportedSeoLocale,
  SUPPORTED_SEO_LOCALES,
  useSeoCatalog,
} from '../../lib/seoCatalog';
import { buildLocalizedPath } from '../../lib/localeRouting';
import '../styles.css';
import './seoPublic.css';

function useSeoHead({ title, description, canonicalPath, alternates, jsonLd }) {
  useEffect(() => {
    document.title = title;

    const html = document.documentElement;
    const previousLang = html.getAttribute('lang');

    function ensureMeta(selector, attributes) {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('meta');
        document.head.appendChild(element);
      }

      Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value);
      });
    }

    function ensureLink(selector, attributes) {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        document.head.appendChild(element);
      }

      Object.entries(attributes).forEach(([key, value]) => {
        element.setAttribute(key, value);
      });
    }

    ensureMeta('meta[name="description"]', {
      name: 'description',
      content: description,
    });
    ensureMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: title,
    });
    ensureMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: description,
    });
    ensureMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website',
    });
    ensureMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: buildAbsoluteUrl(canonicalPath),
    });

    ensureLink('link[rel="canonical"]', {
      rel: 'canonical',
      href: buildAbsoluteUrl(canonicalPath),
    });

    document.head
      .querySelectorAll('link[data-seo-alt="true"]')
      .forEach((element) => element.parentNode?.removeChild(element));

    Object.entries(alternates).forEach(([locale, path]) => {
      const alternate = document.createElement('link');
      alternate.setAttribute('rel', 'alternate');
      alternate.setAttribute('hreflang', locale);
      alternate.setAttribute('href', buildAbsoluteUrl(path));
      alternate.setAttribute('data-seo-alt', 'true');
      document.head.appendChild(alternate);
    });

    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', buildAbsoluteUrl(alternates[DEFAULT_SEO_LOCALE] || canonicalPath));
    xDefault.setAttribute('data-seo-alt', 'true');
    document.head.appendChild(xDefault);

    let jsonLdScript = document.head.querySelector('script[data-seo-jsonld="true"]');
    if (!jsonLdScript) {
      jsonLdScript = document.createElement('script');
      jsonLdScript.setAttribute('type', 'application/ld+json');
      jsonLdScript.setAttribute('data-seo-jsonld', 'true');
      document.head.appendChild(jsonLdScript);
    }
    jsonLdScript.textContent = JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);

    if (canonicalPath.startsWith('/')) {
      const localeFromPath = canonicalPath.split('/')[1] || 'en';
      html.setAttribute('lang', localeFromPath);
    }

    return () => {
      if (previousLang) {
        html.setAttribute('lang', previousLang);
      }
    };
  }, [alternates, canonicalPath, description, jsonLd, title]);
}

function SeoLoadingState() {
  return (
    <div className="seo-page seo-loading">
      <div className="seo-loading-card">
        <div className="seo-spinner" />
        <p>Loading SchoolFlix curriculum content...</p>
      </div>
    </div>
  );
}

function SeoErrorState({ message }) {
  return (
    <div className="seo-page seo-loading">
      <div className="seo-loading-card">
        <p>{message}</p>
      </div>
    </div>
  );
}

function SeoBreadcrumbs({ items }) {
  return (
    <nav className="seo-breadcrumbs" aria-label="Breadcrumb">
      {items.map((item, index) => (
        <React.Fragment key={`${item.path}-${index}`}>
          {index > 0 ? <ChevronRight size={14} /> : null}
          <Link to={item.path}>{item.name}</Link>
        </React.Fragment>
      ))}
    </nav>
  );
}

function SeoHeader({ eyebrow, title, description, accent, ctas }) {
  return (
    <header className="seo-hero" style={{ '--seo-accent': accent }}>
      <div className="seo-hero-copy">
        <span className="seo-eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
        <div className="seo-cta-row">
          {ctas.map((cta) => (
            <Link key={cta.to} to={cta.to} className={`seo-btn ${cta.secondary ? 'secondary' : ''}`}>
              {cta.icon}
              <span>{cta.label}</span>
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
}

function SeoSection({ title, children, action }) {
  return (
    <section className="seo-section">
      <div className="seo-section-head">
        <h2>{title}</h2>
        {action || null}
      </div>
      {children}
    </section>
  );
}

function SeoTopicCard({ locale, grade, subjectSlug, topic, accent }) {
  return (
    <article className="seo-card" style={{ '--seo-accent': accent }}>
      <div className="seo-card-header">
        <span className="seo-card-badge">{formatGradeLabel(grade)}</span>
        <h3>{topic.topic}</h3>
      </div>
      <p>{topic.description}</p>
      <div className="seo-card-links">
        <Link to={buildTopicPath(locale, grade, subjectSlug, topic.topicSlug)}>Topic page</Link>
        <Link to={buildHowToPath(locale, topic.howToSlug)}>How to solve</Link>
        <Link to={buildFirstAidKitPath(locale, grade, subjectSlug, topic.topicSlug)}>First Aid Kit</Link>
      </div>
    </article>
  );
}

function SeoReelCard({ reel, topicLink, howToLink, reelsHubLink, accent }) {
  return (
    <article className="seo-card compact" style={{ '--seo-accent': accent }}>
      <div className="seo-card-header">
        <PlayCircle size={18} />
        <h3>{reel.title}</h3>
      </div>
      <p>{reel.summary}</p>
      <div className="seo-card-meta">
        <span>{reel.duration}</span>
      </div>
      <div className="seo-card-links">
        <Link to={reelsHubLink}>Revision reels</Link>
        <Link to={topicLink}>Topic page</Link>
        <Link to={howToLink}>How-to guide</Link>
      </div>
    </article>
  );
}

function SeoPromptList({ prompts }) {
  return (
    <ul className="seo-list">
      {prompts.map((prompt) => (
        <li key={prompt}>{prompt}</li>
      ))}
    </ul>
  );
}

function SeoStudySteps({ items }) {
  return (
    <ol className="seo-steps">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  );
}

function SeoSidebar({ locale, grade, subjectSlug, topic }) {
  return (
    <aside className="seo-sidebar">
      <div className="seo-sidebar-card">
        <h3>Study next</h3>
        <Link to={buildHowToPath(locale, topic.howToSlug)}>How-to guide</Link>
        <Link to={buildReelsHubPath(locale, grade, subjectSlug)}>Revision reels</Link>
        <Link to={buildFirstAidKitPath(locale, grade, subjectSlug, topic.topicSlug)}>First Aid Kit</Link>
      </div>
      <div className="seo-sidebar-card">
        <h3>SchoolFlix tools</h3>
        <Link to={buildLocalizedPath(locale, 'tutor')}>Ask the AI tutor</Link>
        <Link to={buildLocalizedPath(locale, 'grades')}>Open the First Aid Kit flow</Link>
        <Link to={buildLocalizedPath(locale, 'reels')}>Watch SchoolFlix reels</Link>
      </div>
    </aside>
  );
}

function SeoSubjectPage({ locale, catalog, grade, subjectSlug }) {
  const subject = getSubjectByParams(catalog, grade, subjectSlug);
  const canonicalPath = subject ? buildSubjectPath(locale, grade, subjectSlug) : '/';
  const alternatePaths = subject
    ? createAlternatePaths((nextLocale) => buildSubjectPath(nextLocale, grade, subjectSlug))
    : createAlternatePaths(() => '/');
  const breadcrumbs = [
    { name: 'Home', path: buildLocalizedPath(locale) },
    { name: getLocaleLabel(locale), path: canonicalPath },
    { name: subject ? formatGradeLabel(subject.grade) : 'Curriculum', path: canonicalPath },
    { name: subject ? subject.subject : 'SchoolFlix', path: canonicalPath },
  ];

  useSeoHead({
    title: subject ? `${formatGradeLabel(subject.grade)} ${subject.subject} CAPS Topics | SchoolFlix` : 'SchoolFlix',
    description: subject ? getSubjectSummary(subject) : 'SchoolFlix curriculum content.',
    canonicalPath,
    alternates: alternatePaths,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: buildBreadcrumbs(breadcrumbs),
      },
      createCourseJsonLd(
        subject || {
          grade,
          subject: 'SchoolFlix',
          subjectSlug,
          overview: 'SchoolFlix curriculum content.',
        },
        locale
      ),
    ],
  });

  if (!subject) {
    return <Navigate to={buildLocalizedPath(locale)} replace />;
  }

  const previewTopics = getTopicPreviewItems(subject, 6);

  return (
    <div className="seo-page">
      <SeoBreadcrumbs items={breadcrumbs} />
      <SeoHeader
        eyebrow={`${formatGradeLabel(subject.grade)} • ${subject.subject}`}
        title={`${formatGradeLabel(subject.grade)} ${subject.subject} for CAPS learners`}
        description={getSubjectSummary(subject)}
        accent={subject.accent}
        ctas={[
          {
            to: buildReelsHubPath(locale, subject.grade, subject.subjectSlug),
            label: 'Watch revision reels',
            icon: <Video size={18} />,
          },
          {
            to: buildLocalizedPath(locale, 'tutor'),
            label: 'Ask the AI tutor',
            icon: <Sparkles size={18} />,
            secondary: true,
          },
        ]}
      />

      <div className="seo-grid">
        <div className="seo-main">
          <SeoSection title="Core CAPS topics">
            <div className="seo-card-grid">
              {previewTopics.map((topic) => (
                <SeoTopicCard
                  key={topic.id}
                  locale={locale}
                  grade={subject.grade}
                  subjectSlug={subject.subjectSlug}
                  topic={topic}
                  accent={subject.accent}
                />
              ))}
            </div>
          </SeoSection>

          <SeoSection title="How SchoolFlix supports this subject">
            <div className="seo-info-grid">
              <article className="seo-info-card">
                <BookOpen size={20} />
                <h3>Topic-first learning</h3>
                <p>Every topic can become its own study page, linked back to the subject hub and forward to support tools.</p>
              </article>
              <article className="seo-info-card">
                <Video size={20} />
                <h3>Revision reels</h3>
                <p>Use short-form reels to revise high-frequency concepts before moving into deeper explanations.</p>
              </article>
              <article className="seo-info-card">
                <HelpCircle size={20} />
                <h3>First Aid Kit</h3>
                <p>When learners are stuck, move them into guided support instead of letting them bounce away.</p>
              </article>
            </div>
          </SeoSection>
        </div>

        <aside className="seo-sidebar">
          <div className="seo-sidebar-card">
            <h3>Quick links</h3>
            <Link to={buildReelsHubPath(locale, subject.grade, subject.subjectSlug)}>Revision reels hub</Link>
            <Link to={buildLocalizedPath(locale, 'subjects')}>Browse all subjects</Link>
            <Link to={buildLocalizedPath(locale, 'grades')}>Open First Aid Kit flow</Link>
          </div>
          <div className="seo-sidebar-card">
            <h3>Available language path</h3>
            <p>This page is available under {SUPPORTED_SEO_LOCALES.length} locale prefixes with alternate links and canonical handling.</p>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SeoTopicPage({ locale, catalog, grade, subjectSlug, topicSlug }) {
  const topic = getTopicByParams(catalog, grade, subjectSlug, topicSlug);
  const canonicalPath = topic ? buildTopicPath(locale, grade, subjectSlug, topicSlug) : '/';
  const alternatePaths = topic
    ? createAlternatePaths((nextLocale) => buildTopicPath(nextLocale, grade, subjectSlug, topicSlug))
    : createAlternatePaths(() => '/');
  const breadcrumbs = [
    { name: 'Home', path: buildLocalizedPath(locale) },
    { name: topic ? formatGradeLabel(topic.grade) : 'Curriculum', path: topic ? buildSubjectPath(locale, topic.grade, topic.subjectSlug) : '/' },
    { name: topic ? topic.subject : 'SchoolFlix', path: topic ? buildSubjectPath(locale, topic.grade, topic.subjectSlug) : '/' },
    { name: topic ? topic.topic : 'Topic', path: canonicalPath },
  ];
  const relatedTopics = topic ? getRelatedTopics(catalog, topic, 3) : [];

  useSeoHead({
    title: topic ? `${topic.topic} | ${formatGradeLabel(topic.grade)} ${topic.subject} | SchoolFlix` : 'SchoolFlix',
    description: topic ? getTopicSummary(topic) : 'SchoolFlix topic content.',
    canonicalPath,
    alternates: alternatePaths,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: buildBreadcrumbs(breadcrumbs),
      },
      createCourseJsonLd(
        topic
          ? getSubjectByParams(catalog, topic.grade, topic.subjectSlug) || {
              grade: topic.grade,
              subject: topic.subject,
              subjectSlug: topic.subjectSlug,
              overview: getTopicSummary(topic),
            }
          : {
              grade,
              subject: 'SchoolFlix',
              subjectSlug,
              overview: 'SchoolFlix topic content.',
            },
        locale
      ),
    ],
  });

  if (!topic) {
    return <Navigate to={buildLocalizedPath(locale)} replace />;
  }

  return (
    <div className="seo-page">
      <SeoBreadcrumbs items={breadcrumbs} />
      <SeoHeader
        eyebrow={`${formatGradeLabel(topic.grade)} • ${topic.subject}`}
        title={topic.topic}
        description={getTopicSummary(topic)}
        accent={topic.subjectAccent}
        ctas={[
          {
            to: buildLocalizedPath(locale, 'tutor'),
            label: 'Ask the AI tutor',
            icon: <Sparkles size={18} />,
          },
          {
            to: buildFirstAidKitPath(locale, topic.grade, topic.subjectSlug, topic.topicSlug),
            label: 'Open First Aid Kit',
            icon: <HelpCircle size={18} />,
            secondary: true,
          },
        ]}
      />

      <div className="seo-grid">
        <div className="seo-main">
          <SeoSection title="CAPS-aligned topic summary">
            <p className="seo-lead">{topic.description}</p>
          </SeoSection>

          <SeoSection title="Worked example flow">
            <SeoStudySteps items={topic.workedExample} />
          </SeoSection>

          <SeoSection title="Common mistakes learners make">
            <SeoPromptList prompts={topic.commonMistakes} />
          </SeoSection>

          <SeoSection title="Revision reels for this topic">
            <div className="seo-card-grid">
              {topic.reels.map((reel) => (
                <SeoReelCard
                  key={reel.id}
                  reel={reel}
                  topicLink={buildTopicPath(locale, topic.grade, topic.subjectSlug, topic.topicSlug)}
                  howToLink={buildHowToPath(locale, topic.howToSlug)}
                  reelsHubLink={buildReelsHubPath(locale, topic.grade, topic.subjectSlug)}
                  accent={topic.subjectAccent}
                />
              ))}
            </div>
          </SeoSection>

          <SeoSection title="Related topics">
            <div className="seo-card-grid">
              {relatedTopics.map((entry) => (
                <SeoTopicCard
                  key={entry.id}
                  locale={locale}
                  grade={topic.grade}
                  subjectSlug={topic.subjectSlug}
                  topic={entry}
                  accent={topic.subjectAccent}
                />
              ))}
            </div>
          </SeoSection>
        </div>

        <SeoSidebar locale={locale} grade={topic.grade} subjectSlug={topic.subjectSlug} topic={topic} />
      </div>
    </div>
  );
}

function SeoHowToPage({ locale, catalog, howToSlug }) {
  const topic = getTopicByHowToSlug(catalog, howToSlug);
  const canonicalPath = topic ? buildHowToPath(locale, howToSlug) : '/';
  const alternatePaths = topic
    ? createAlternatePaths((nextLocale) => buildHowToPath(nextLocale, howToSlug))
    : createAlternatePaths(() => '/');
  const breadcrumbs = [
    { name: 'Home', path: buildLocalizedPath(locale) },
    { name: topic ? formatGradeLabel(topic.grade) : 'Curriculum', path: topic ? buildSubjectPath(locale, topic.grade, topic.subjectSlug) : '/' },
    { name: topic ? topic.subject : 'SchoolFlix', path: topic ? buildSubjectPath(locale, topic.grade, topic.subjectSlug) : '/' },
    { name: topic ? topic.topic : 'Topic', path: topic ? buildTopicPath(locale, topic.grade, topic.subjectSlug, topic.topicSlug) : '/' },
    { name: 'How to solve', path: canonicalPath },
  ];

  useSeoHead({
    title: topic ? `${createHowToHeading(topic)} | SchoolFlix` : 'SchoolFlix',
    description: topic ? getHowToSummary(topic) : 'SchoolFlix how-to content.',
    canonicalPath,
    alternates: alternatePaths,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: buildBreadcrumbs(breadcrumbs),
      },
      createHowToJsonLd(
        topic || {
          topic: 'SchoolFlix topic',
          grade: '12',
          subject: 'SchoolFlix',
          workedExample: ['Open the topic page.', 'Review the explanation.', 'Continue learning with SchoolFlix.'],
          howToSlug,
        },
        locale
      ),
    ],
  });

  if (!topic) {
    return <Navigate to={buildLocalizedPath(locale)} replace />;
  }

  return (
    <div className="seo-page">
      <SeoBreadcrumbs items={breadcrumbs} />
      <SeoHeader
        eyebrow={`${formatGradeLabel(topic.grade)} • ${topic.subject}`}
        title={createHowToHeading(topic)}
        description={getHowToSummary(topic)}
        accent={topic.subjectAccent}
        ctas={[
          {
            to: buildTopicPath(locale, topic.grade, topic.subjectSlug, topic.topicSlug),
            label: 'Open topic page',
            icon: <BookOpen size={18} />,
          },
          {
            to: buildLocalizedPath(locale, 'tutor'),
            label: 'Ask the AI tutor',
            icon: <Sparkles size={18} />,
            secondary: true,
          },
        ]}
      />

      <div className="seo-grid">
        <div className="seo-main">
          <SeoSection title="Method">
            <SeoStudySteps items={topic.workedExample} />
          </SeoSection>

          <SeoSection title="Common mistakes">
            <SeoPromptList prompts={topic.commonMistakes} />
          </SeoSection>

          <SeoSection title="Study next">
            <div className="seo-info-grid">
              <article className="seo-info-card">
                <Compass size={20} />
                <h3>Return to the topic page</h3>
                <p>Go back to the full topic page for broader context, related concepts, and internal links.</p>
              </article>
              <article className="seo-info-card">
                <Video size={20} />
                <h3>Watch quick revision reels</h3>
                <p>Use reels when you need a faster visual recap before practicing again.</p>
              </article>
              <article className="seo-info-card">
                <HelpCircle size={20} />
                <h3>Use the First Aid Kit</h3>
                <p>Switch into guided study support if this problem pattern still feels unstable.</p>
              </article>
            </div>
          </SeoSection>
        </div>

        <SeoSidebar locale={locale} grade={topic.grade} subjectSlug={topic.subjectSlug} topic={topic} />
      </div>
    </div>
  );
}

function SeoReelsHubPage({ locale, catalog, grade, subjectSlug }) {
  const subject = getSubjectByParams(catalog, grade, subjectSlug);
  const canonicalPath = subject ? buildReelsHubPath(locale, grade, subjectSlug) : '/';
  const alternatePaths = subject
    ? createAlternatePaths((nextLocale) => buildReelsHubPath(nextLocale, grade, subjectSlug))
    : createAlternatePaths(() => '/');
  const breadcrumbs = [
    { name: 'Home', path: buildLocalizedPath(locale) },
    { name: subject ? formatGradeLabel(subject.grade) : 'Curriculum', path: subject ? buildSubjectPath(locale, subject.grade, subject.subjectSlug) : '/' },
    { name: subject ? subject.subject : 'SchoolFlix', path: subject ? buildSubjectPath(locale, subject.grade, subject.subjectSlug) : '/' },
    { name: 'Revision reels', path: canonicalPath },
  ];
  const previewTopics = subject ? getTopicPreviewItems(subject, 4) : [];

  useSeoHead({
    title: subject ? `${formatGradeLabel(subject.grade)} ${subject.subject} Revision Reels | SchoolFlix` : 'SchoolFlix',
    description: subject ? getReelsSummary(subject) : 'SchoolFlix revision reels.',
    canonicalPath,
    alternates: alternatePaths,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: buildBreadcrumbs(breadcrumbs),
      },
      createVideoJsonLd(
        subject || {
          grade,
          subject: 'SchoolFlix',
          subjectSlug,
          image: '/logo192.png',
        },
        locale
      ),
    ],
  });

  if (!subject) {
    return <Navigate to={buildLocalizedPath(locale)} replace />;
  }

  return (
    <div className="seo-page">
      <SeoBreadcrumbs items={breadcrumbs} />
      <SeoHeader
        eyebrow={`${formatGradeLabel(subject.grade)} • ${subject.subject}`}
        title={`${formatGradeLabel(subject.grade)} ${subject.subject} revision reels`}
        description={getReelsSummary(subject)}
        accent={subject.accent}
        ctas={[
          {
            to: buildLocalizedPath(locale, 'reels'),
            label: 'Open SchoolFlix reels',
            icon: <Video size={18} />,
          },
          {
            to: buildLocalizedPath(locale, 'tutor'),
            label: 'Ask the AI tutor',
            icon: <Sparkles size={18} />,
            secondary: true,
          },
        ]}
      />

      <SeoSection title="Featured reel clusters">
        <div className="seo-card-grid">
          {previewTopics.map((topic) =>
            topic.reels.map((reel) => (
              <SeoReelCard
                key={reel.id}
                reel={reel}
                topicLink={buildTopicPath(locale, subject.grade, subject.subjectSlug, topic.topicSlug)}
                howToLink={buildHowToPath(locale, topic.howToSlug)}
                reelsHubLink={canonicalPath}
                accent={subject.accent}
              />
            ))
          )}
        </div>
      </SeoSection>
    </div>
  );
}

function SeoFirstAidKitPage({ locale, catalog, grade, subjectSlug, topicSlug }) {
  const topic = getTopicByParams(catalog, grade, subjectSlug, topicSlug);
  const canonicalPath = topic ? buildFirstAidKitPath(locale, grade, subjectSlug, topicSlug) : '/';
  const alternatePaths = topic
    ? createAlternatePaths((nextLocale) => buildFirstAidKitPath(nextLocale, grade, subjectSlug, topicSlug))
    : createAlternatePaths(() => '/');
  const breadcrumbs = [
    { name: 'Home', path: buildLocalizedPath(locale) },
    { name: topic ? formatGradeLabel(topic.grade) : 'Curriculum', path: topic ? buildSubjectPath(locale, topic.grade, topic.subjectSlug) : '/' },
    { name: topic ? topic.subject : 'SchoolFlix', path: topic ? buildSubjectPath(locale, topic.grade, topic.subjectSlug) : '/' },
    { name: topic ? topic.topic : 'Topic', path: topic ? buildTopicPath(locale, topic.grade, topic.subjectSlug, topic.topicSlug) : '/' },
    { name: 'First Aid Kit', path: canonicalPath },
  ];

  useSeoHead({
    title: topic ? `${createFirstAidHeading(topic)} | SchoolFlix` : 'SchoolFlix',
    description: topic ? getFirstAidSummary(topic) : 'SchoolFlix study support.',
    canonicalPath,
    alternates: alternatePaths,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: buildBreadcrumbs(breadcrumbs),
      },
      createFaqJsonLd(
        topic || {
          topic: 'SchoolFlix topic',
          grade: grade || '12',
          subject: 'SchoolFlix',
          subjectSlug,
          topicSlug: topicSlug || 'topic',
        },
        locale
      ),
    ],
  });

  if (!topic) {
    return <Navigate to={buildLocalizedPath(locale)} replace />;
  }

  return (
    <div className="seo-page">
      <SeoBreadcrumbs items={breadcrumbs} />
      <SeoHeader
        eyebrow={`${formatGradeLabel(topic.grade)} • ${topic.subject}`}
        title={createFirstAidHeading(topic)}
        description={getFirstAidSummary(topic)}
        accent={topic.subjectAccent}
        ctas={[
          {
            to: buildLocalizedPath(locale, 'grades'),
            label: 'Open First Aid Kit flow',
            icon: <HelpCircle size={18} />,
          },
          {
            to: buildLocalizedPath(locale, 'tutor'),
            label: 'Ask the AI tutor',
            icon: <Sparkles size={18} />,
            secondary: true,
          },
        ]}
      />

      <div className="seo-grid">
        <div className="seo-main">
          <SeoSection title="Typical struggle points">
            <SeoPromptList prompts={topic.commonMistakes} />
          </SeoSection>

          <SeoSection title="Suggested prompts">
            <SeoPromptList prompts={topic.firstAidPrompts} />
          </SeoSection>

          <SeoSection title="Study recovery checklist">
            <SeoStudySteps items={topic.supportChecklist} />
          </SeoSection>
        </div>

        <SeoSidebar locale={locale} grade={topic.grade} subjectSlug={topic.subjectSlug} topic={topic} />
      </div>
    </div>
  );
}

function parseSeoRoute(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const locale = segments[0];
  const rest = segments.slice(1);

  if (!isSupportedSeoLocale(locale)) {
    return null;
  }

  if (rest.length === 2 && /^grade-\d+$/.test(rest[0])) {
    return {
      type: 'subject',
      locale,
      grade: rest[0].replace('grade-', ''),
      subjectSlug: rest[1],
    };
  }

  if (rest.length === 3 && /^grade-\d+$/.test(rest[0]) && rest[2] === 'revision-reels') {
    return {
      type: 'reels',
      locale,
      grade: rest[0].replace('grade-', ''),
      subjectSlug: rest[1],
    };
  }

  if (rest.length === 4 && rest[0] === 'first-aid-kit' && /^grade-\d+$/.test(rest[1])) {
    return {
      type: 'first-aid',
      locale,
      grade: rest[1].replace('grade-', ''),
      subjectSlug: rest[2],
      topicSlug: rest[3],
    };
  }

  if (rest.length === 2 && rest[0] === 'how-to-solve') {
    return {
      type: 'how-to',
      locale,
      howToSlug: rest[1],
    };
  }

  if (rest.length === 3 && /^grade-\d+$/.test(rest[0])) {
    return {
      type: 'topic',
      locale,
      grade: rest[0].replace('grade-', ''),
      subjectSlug: rest[1],
      topicSlug: rest[2],
    };
  }

  return null;
}

export default function SeoPublicRouter() {
  const { locale } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  const routeMatch = useMemo(() => parseSeoRoute(location.pathname), [location.pathname]);
  const seoLocale = getSeoLocale(locale);
  const catalog = useSeoCatalog();

  useEffect(() => {
    if (isSupportedSeoLocale(seoLocale) && i18n.language !== seoLocale) {
      i18n.changeLanguage(seoLocale).catch((error) => {
        console.error('SEO route language switch failed:', error);
      });
    }
  }, [i18n, seoLocale]);

  if (!routeMatch) {
    return <Navigate to={buildLocalizedPath(seoLocale)} replace />;
  }

  if (catalog.loading) {
    return <SeoLoadingState />;
  }

  if (catalog.error) {
    return <SeoErrorState message={catalog.error} />;
  }

  switch (routeMatch.type) {
    case 'subject':
      return <SeoSubjectPage locale={routeMatch.locale} catalog={catalog} grade={routeMatch.grade} subjectSlug={routeMatch.subjectSlug} />;
    case 'topic':
      return (
        <SeoTopicPage
          locale={routeMatch.locale}
          catalog={catalog}
          grade={routeMatch.grade}
          subjectSlug={routeMatch.subjectSlug}
          topicSlug={routeMatch.topicSlug}
        />
      );
    case 'how-to':
      return <SeoHowToPage locale={routeMatch.locale} catalog={catalog} howToSlug={routeMatch.howToSlug} />;
    case 'reels':
      return <SeoReelsHubPage locale={routeMatch.locale} catalog={catalog} grade={routeMatch.grade} subjectSlug={routeMatch.subjectSlug} />;
    case 'first-aid':
      return (
        <SeoFirstAidKitPage
          locale={routeMatch.locale}
          catalog={catalog}
          grade={routeMatch.grade}
          subjectSlug={routeMatch.subjectSlug}
          topicSlug={routeMatch.topicSlug}
        />
      );
    default:
      return <Navigate to={buildLocalizedPath(seoLocale)} replace />;
  }
}
